import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (next === current) return toast.error("New password must be different from current");
    setBusy(true);
    try {
      await api.post("/auth/change_password", { current_password: current, new_password: next });
      toast.success("Password updated. Please sign in again with your new password.");
      setCurrent(""); setNext(""); setConfirm("");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="change-password-root">
      <div>
        <div className="label-caps">Account</div>
        <h1 className="font-heading text-3xl sm:text-4xl">Change Password</h1>
        <div className="text-[color:var(--text-secondary)] mt-2 text-sm">
          Signed in as <span className="text-[color:var(--text-primary)]">{user?.name}</span> ({user?.email})
        </div>
      </div>

      <form onSubmit={submit} className="hos-card p-6 space-y-4">
        <Field label="Current password" icon={Lock} value={current} onChange={setCurrent} testid="cp-current"/>
        <Field label="New password" icon={KeyRound} value={next} onChange={setNext} testid="cp-new" hint="At least 6 characters"/>
        <Field label="Confirm new password" icon={KeyRound} value={confirm} onChange={setConfirm} testid="cp-confirm"/>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" className="hos-btn-primary" disabled={busy} data-testid="cp-submit">
            {busy ? "Updating…" : "Update password"}
          </button>
          <button type="button" className="hos-btn-outline" onClick={() => nav(-1)} disabled={busy}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, value, onChange, testid, hint }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-[color:var(--text-secondary)]">{label}</label>
      <div className="relative mt-1">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]"/>
        <input
          data-testid={testid}
          type="password"
          className="hos-input pl-10"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
        />
      </div>
      {hint && <div className="text-[11px] text-[color:var(--text-disabled)] mt-1">{hint}</div>}
    </div>
  );
}
